<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref } from "vue"
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import type { Subscription } from "@modules/espace-entreprise/types/subscription"
import {
    formatSubscriptions,
    getLegacySubscriptions,
} from "@modules/espace-entreprise/helpers/subscription.helper"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            hors_taxe: "* Tous les prix sont indiqués hors taxes",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
type Props = {
    /**
     * Liste de tous les abonnements
     */
    subscriptions: Subscription[]
    /**
     * Nombre de sources actives
     */
    sources: number
}

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<Props>()

// ================================
// STATE
// ================================
const isDialogVisible = ref(false)

// ================================
// METHODS
// ================================

/**
 * Gère l'ouverture du dialog des sources de facturation
 */
function onClickSource() {
    isDialogVisible.value = true
}

// ================================
// COMPUTED
// ================================

/**
 * Récupère les abonnements formatés (Energy Management / Energy Analytics avec leurs modules)
 */
const getSubscriptions = computed(() => {
    return formatSubscriptions(props.subscriptions)
})

/**
 * Récupère les abonnements legacy (anciens abonnements)
 */
const getLegacySubscriptionsList = computed(() => {
    return getLegacySubscriptions(props.subscriptions)
})

/**
 * Combine les abonnements formatés et legacy pour l'affichage
 */
const getAllSubscriptions = computed(() => {
    return [...getSubscriptions.value, ...getLegacySubscriptionsList.value]
})
</script>

<template>
    <div class="company-subscriptions-list w-full">
        <!-- ================================ -->
        <!-- LISTE DES ABONNEMENTS -->
        <!-- ================================ -->
        <div class="flex flex-col gap-0">
            <CompanySubscriptionsListItem
                v-for="(subscription, index) in getAllSubscriptions"
                :key="`subscription-${index}`"
                :subscription="subscription"
                :sources="sources"
                @click:source="onClickSource"
            />
        </div>

        <!-- ================================ -->
        <!-- MESSAGE HORS TAXE -->
        <!-- ================================ -->
        <p
            v-if="subscriptions.length > 0"
            class="company-subscriptions-list__star text-sm text-gray-600 mt-4"
        >
            {{ t("hors_taxe") }}
        </p>

        <!-- ================================ -->
        <!-- DIALOG SOURCES DE FACTURATION -->
        <!-- ================================ -->
        <CompanyBillingSourceDialog v-model:visible="isDialogVisible" />
    </div>
</template>
